import type { Node } from 'web-tree-sitter';
import type { LanguageId } from '../pipeline/types';
import { getParserFor } from './tree-sitter';

// String/template literals only — the node types that actually correspond to
// "long arrays, base64 blobs, embedded data" per the spec's examples. Object
// and array *literals* are deliberately left alone: unlike a string blob,
// their contents are often meaningful code structure, and deciding "data vs.
// structure" generically is out of scope here.
const STRING_TYPES: Record<LanguageId, string[]> = {
  javascript: ['string', 'template_string'],
  typescript: ['string', 'template_string'],
  python: ['string'],
};

export async function elideLargeLiterals(content: string, languageId: LanguageId, thresholdChars: number): Promise<string> {
  const parser = await getParserFor(languageId);
  const tree = parser.parse(content);
  if (!tree) return content;

  const stringTypes = STRING_TYPES[languageId];
  const replacements: Array<[number, number, string]> = [];

  const walk = (node: Node) => {
    if (stringTypes.includes(node.type)) {
      const length = node.endIndex - node.startIndex;
      if (length > thresholdChars) {
        const kb = (length / 1024).toFixed(1);
        replacements.push([node.startIndex, node.endIndex, `/* …elided: ${kb}KB string… */`]);
      }
      return; // don't recurse into a string's own (rare, template-substitution) children once elided
    }
    for (const child of node.children) if (child) walk(child);
  };
  walk(tree.rootNode);

  let result = content;
  for (const [start, end, placeholder] of [...replacements].sort((a, b) => b[0] - a[0])) {
    result = result.slice(0, start) + placeholder + result.slice(end);
  }
  return result;
}
